import React, { useState } from 'react';

import { ProductTelemetryController } from '../../product-telemetry/product-telemetry-controller';
import { UiButton } from '../../ui/ui-button';

import { EditMessageTemplateDialog } from './edit-message-template-dialog';
import { InlineTextMessageTemplate } from './inline-text-message-template';

type InputProps<T> = {
  templates: Array<T>;
  parser: MessageTemplateParser;
  recipientType: ContactType;
  templateType: TemplateType;

  editorCaption?: string;
  preventMessageParsing?: boolean;
  useRichText?: boolean;
  addButtonText?: string;

  onSelect: (messageBody: string, template: T) => void;
  onSave: (template: T, options?: any) => void;
  onDelete: (template: T) => void;
};

export const InlineTemplateList = <T extends Template>({
  templates,
  parser,
  onSelect,
  onSave,
  onDelete,
  preventMessageParsing,
  editorCaption,
  useRichText,
  addButtonText,
  recipientType,
  templateType,
}: InputProps<T>) => {
  const [isAddingNewTemplate, setIsAddingNewTemplate] = useState<boolean>(false);
  const toggleIsAdding = () => setIsAddingNewTemplate(!isAddingNewTemplate);
  const handleNewMessageSave = (template: T, options?: any) => {
    setIsAddingNewTemplate(false);
    onSave(template, options);
  };
  return (
    <>
      {templates.map((t) => {
        const messageBody = preventMessageParsing ? t.template : parser.parse(t.template);
        const handleSelect = () => {
          ProductTelemetryController.logEvent('clicked use saved message template');
          onSelect(messageBody, t);
        };
        return (
          <div key={`template-${t.template}`} className="u-width-100 u-spread">
            <div className="u-width-100 u-overflow-hidden">
              <InlineTextMessageTemplate
                template={t}
                messageBody={messageBody}
                parser={parser}
                onSave={onSave}
                onDelete={onDelete}
                useRichText={useRichText}
                recipientType={recipientType}
                templateType={templateType}
              />
            </div>
            <UiButton
              onClick={handleSelect}
              variant="secondary"
              className="u-margin-left-md"
              dataCy="use-template"
            >
              Use
            </UiButton>
          </div>
        );
      })}
      <div className="u-margin-top-md">
        <UiButton variant="tertiary" onClick={toggleIsAdding} size="sm">
          {addButtonText || 'Add Saved Message +'}
        </UiButton>
      </div>
      {isAddingNewTemplate ? (
        <EditMessageTemplateDialog
          caption={editorCaption}
          onSave={handleNewMessageSave}
          onClose={toggleIsAdding}
          parser={parser}
          useRichText={useRichText}
          recipientType={recipientType}
          templateType={templateType}
        />
      ) : null}
    </>
  );
};
